import React, { ReactElement, ReactNode, useMemo, useState } from "react";
import {
  Table,
  Button,
  Form,
  Pagination,
  Modal,
  InputGroup,
} from "react-bootstrap";
import {
  PencilSquare,
  Trash3Fill,
  Eye,
  CaretUpFill,
  CaretDownFill,
} from "react-bootstrap-icons";
import "./commontable.scss";

export interface TableColumn {
  key: string;
  label: string;
  dataType?: "string" | "boolean" | "button" | "image" | "icon" | "toggle";
  sortable?: boolean;
  button?: {
    icon?: JSX.Element;
    label?: string | JSX.Element | ((item: any) => string | JSX.Element);
    onClick: (item: any) => void;
    disabled?: (item: any) => boolean;
  };
  onClick?: (item: any) => void;
  render?: (item: any) => React.ReactNode | JSX.Element | string;
}

interface CommonTableProps<T> {
  data: T[];
  columns: TableColumn[];
  onView?: (item: T) => void;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  onStatusToggle?: (item: T) => void;
  onToggle?: (item: T) => void;
  pagination?: boolean;
  rowsPerPage?: number;
  actions?: {
    view?: boolean;
    edit?: boolean;
    delete?: boolean;
    checkbox?: boolean;
    status?: boolean | number;
    showModal?: boolean;
  };
  onPageChange?: (page: number) => void;
  onCheckboxChange?: (selectedIds: Set<number>) => void;
  apiPagination?: boolean;
  totalPageCount?: number;
  searchable?: boolean;
  apiSearch?: boolean;
  onSearch?: (query: string) => void;
}

const CommonTable = <T extends { id: number; status?: any }>({
  data,
  columns,
  onToggle,
  onView,
  onEdit = () => {},
  onDelete,
  onStatusToggle,
  pagination = true,
  rowsPerPage = 10,
  actions = {
    view: false,
    edit: false,
    delete: false,
    checkbox: false,
    status: false,
    showModal: true,
  },
  onPageChange,
  onCheckboxChange,
  apiPagination = false,
  totalPageCount = 0,
  searchable = true,
  apiSearch = false,
  onSearch
}: CommonTableProps<T>) => {
  const [page, setPage] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState<React.ReactNode>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "ascending" | "descending";
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  const handleCheckboxChange = (id: number) => {
    const updatedSelection = new Set(selectedRows);
    if (updatedSelection.has(id)) {
      updatedSelection.delete(id);
    } else {
      updatedSelection.add(id);
    }
    setSelectedRows(updatedSelection);
    onCheckboxChange?.(updatedSelection);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSelectedRows = new Set<number>();
    if (e.target.checked) {
      safeFilteredData.forEach((item) => newSelectedRows.add(item.id));
    }
    setSelectedRows(newSelectedRows);
    onCheckboxChange?.(newSelectedRows);
    e.currentTarget.blur();
  };

  // const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const newSelectedRows = new Set<number>();

  //   if (e.target.checked) {
  //     safeFilteredData.forEach((item) => newSelectedRows.add(item.id));
  //   }

  //   setSelectedRows(newSelectedRows);
  //   onCheckboxChange?.(newSelectedRows);
  // };

  const handleChangePage = (newPage: number) => {
    // console.log("NEW PAGE", newPage)
    setPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };
  const handleShowModal = (content: React.ReactNode) => {
    setModalContent(content);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalContent(null);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if(apiSearch && onSearch){
      onSearch(query);
    }
    setPage(0);
  };

  // const sortedData = useMemo(() => {
  //   if (sortConfig !== null) {
  //     return [...data].sort((a, b) => {
  //       const aValue = a[sortConfig.key as keyof T];
  //       const bValue = b[sortConfig.key as keyof T];

  //       // console.log("SORTCONFIG",sortConfig);
        
  //       // console.log("AVALUE", aValue);
  //       // console.log("BVALUE", bValue);
        

  //     //    if (typeof aValue === 'string' && typeof bValue === 'string') {
  //     //   return sortConfig.direction === "ascending"
  //     //     ? aValue.localeCompare(bValue)
  //     //     : bValue.localeCompare(aValue);
  //     // }

  //     const getTextContent = (value: any): string => {
  //       if (React.isValidElement(value)) {
  //         // Cast 'value' as a ReactElement to access props safely
  //         const element = value as ReactElement<{ children: ReactNode }>;

  //         // Assuming text is the child of the JSX element, extract text content
  //         return typeof element.props.children === 'string'
  //           ? element.props.children
  //           : '';
  //       }
  //       // If it's not a JSX element, return the value as-is (for other data types)
  //       return value;
  //     };

  //     const textA = getTextContent(aValue);
  //     const textB = getTextContent(bValue);

  //     // Sort strings
  //     if (typeof textA === 'string' && typeof textB === 'string') {
  //       return sortConfig.direction === 'ascending'
  //         ? textA.localeCompare(textB)
  //         : textB.localeCompare(textA);
  //     }

  //       if (aValue < bValue) {
  //         return sortConfig.direction === "ascending" ? -1 : 1;
  //       }
  //       if (aValue > bValue) {
  //         return sortConfig.direction === "ascending" ? 1 : -1;
  //       }
  //       return 0;
  //     });
  //   }
  //   return data;
  // }, [data, sortConfig]);

  const sortedData = useMemo(() => {
    if (sortConfig !== null) {
      return [...data].sort((a, b) => {
        const aValue = a[sortConfig.key as keyof T];
        const bValue = b[sortConfig.key as keyof T];
  
        // Helper function to extract text content from a ReactNode or convert to a string
        const getTextContent = (value: any): string => {
          if (React.isValidElement(value)) {
            const element = value as ReactElement<{ children: ReactNode }>;
            return typeof element.props.children === 'string'
              ? element.props.children
              : '';
          }
          return String(value); // Convert non-ReactNode values to string
        };
  
        const textA = getTextContent(aValue);
        const textB = getTextContent(bValue);
  
        // Try to convert strings to numbers if they are numeric
        const numA = parseFloat(textA);
        const numB = parseFloat(textB);
  
        const isANumeric = !isNaN(numA);
        const isBNumeric = !isNaN(numB);
  
        // If both are numbers, sort numerically
        if (isANumeric && isBNumeric) {
          return sortConfig.direction === 'ascending'
            ? numA - numB
            : numB - numA;
        }
  
        // Otherwise, sort as strings
        return sortConfig.direction === 'ascending'
          ? textA.localeCompare(textB)
          : textB.localeCompare(textA);
      });
    }
    return data;
  }, [data, sortConfig]);
  

  // console.log("SORTED DATA",sortedData)

  // const sortedData = useMemo(() => {
  //   if (sortConfig !== null) {
  //     return [...data].sort((a, b) => {
  //       const aValue = a[sortConfig.key as keyof T];
  //       const bValue = b[sortConfig.key as keyof T];
  
  //       // Recursive function to extract text from ReactNode
  //       const extractTextContent = (node: ReactNode): string => {
  //         if (typeof node === 'string') {
  //           return node;
  //         }
  
  //         if (Array.isArray(node)) {
  //           // If it's an array of children, recursively extract text from each child
  //           return node.map(extractTextContent).join('');
  //         }
  
  //         if (React.isValidElement(node)) {
  //           // If it's a valid JSX element, recursively extract text from its children
  //           return extractTextContent(node.props.children);
  //         }
  
  //         // If it's not a string or JSX element, return an empty string
  //         return '';
  //       };
  
  //       const textA = extractTextContent(aValue as unknown as ReactNode);
  //       const textB = extractTextContent(bValue as unknown as ReactNode);
  
  //       // Sort strings
  //       if (typeof textA === 'string' && typeof textB === 'string') {
  //         return sortConfig.direction === 'ascending'
  //           ? textA.localeCompare(textB)
  //           : textB.localeCompare(textA);
  //       }
  
  //       // Sort other types (numbers, dates, etc.)
  //       if (aValue < bValue) {
  //         return sortConfig.direction === 'ascending' ? -1 : 1;
  //       }
  //       if (aValue > bValue) {
  //         return sortConfig.direction === 'ascending' ? 1 : -1;
  //       }
  //       return 0;
  //     });
  //   }
  //   return data;
  // }, [data, sortConfig]);

  const handleEdit = (item: any) => {
    if (!showModal) {
      onEdit(item);
      return;
    }
    handleShowModal(
      <EditModal item={item} onEdit={onEdit} onClose={handleCloseModal} />
    );
  };

  const filteredData = useMemo(() => {
    if ( !apiSearch && searchQuery.trim()) {
      // console.log(searchQuery);
      
      return sortedData.filter((item) =>
        columns.some((column) => {
          
          if (column && column.dataType !== "image") {
            const itemValue = String(item[column.key as keyof T])
              .toLowerCase()
              .trim();
            const searchValue = searchQuery.toLowerCase().trim();
            return itemValue.includes(searchValue);
          }
          return false;
        })
      );
    }
    return sortedData;
  }, [sortedData, searchQuery, columns]);

  // console.log("FILTERED DATA", filteredData)

  const safeFilteredData = Array.isArray(filteredData) ? filteredData : [];
//  console.log("SAFE FILTERED DATA", safeFilteredData)

  // const totalPages = apiPagination
  //   ? Math.ceil(totalPageCount/rowsPerPage)
  //   : Math.ceil(safeFilteredData.length / rowsPerPage);

  const getTotalPages = ()=>{
    if( totalPageCount > 0 && searchQuery.length === 0){
      return Math.ceil(totalPageCount/rowsPerPage)
    }
    else if(totalPageCount > 0 && apiSearch){
      return Math.ceil(totalPageCount/rowsPerPage)
    }
    // else if(sortConfig !== null || searchQuery.length > 0){
      return Math.ceil(safeFilteredData.length / rowsPerPage)
    // }
  }
  
  const totalPages = getTotalPages();

  // const totalPages = totalPageCount > 0 
  // ? Math.ceil(totalPageCount/rowsPerPage)
  // : Math.ceil(safeFilteredData.length / rowsPerPage);


  const paginatedData = apiPagination
    ? safeFilteredData
    : pagination
    ? safeFilteredData.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
    : safeFilteredData;

  // console.log("Paginated DATA:",paginatedData, "Total PAGES:",totalPages);
  const requestSort = (key: string) => {
    let direction: "ascending" | "descending" = "ascending";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const showActionsColumn = actions.view || actions.edit || actions.delete;

  return (
    <div className="table-responsive mx-3 my-3">
     <InputGroup style={{ width: "340px" }} className="mb-3 float-end">
        <Form.Control
          type="text"
          placeholder="Search..."
          value={searchQuery}
          style={{fontSize: "14px"}}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </InputGroup> 
     {/* { apiSearch 
     ? 
     <InputGroup style={{ width: "340px" }} className="mb-3 float-end">
        <Form.Control
          type="text"
          placeholder="Search..."
          value={apiSearchQuery}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </InputGroup>
     : <InputGroup style={{ width: "340px" }} className="mb-3 float-end">
        <Form.Control
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </InputGroup>} */}
      <Table striped bordered hover className="tableheader">
        <thead>
          <tr>
            {actions.checkbox && (
              <th style={{ width: "50px" }}>
                <Form.Check
                  type="checkbox"
                  id="select-all-checkbox"
                  onChange={handleSelectAll}
                  checked={
                    safeFilteredData.length > 0 &&
                    selectedRows.size === safeFilteredData.length
                  }
                />
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.key}
                onClick={
                  column.sortable ? () => requestSort(column.key) : undefined
                }
                style={{ cursor: column.sortable ? "pointer" : "default" }}
              >
                <span className="d-flex align-items-center justify-content-center">
                {column.label}
                {column.sortable && (
                  <span className="ms-1"
                  style={{
                    fontSize: "10px"
                  }}
                  >
                    {sortConfig?.key === column.key ? (
                      sortConfig.direction === "ascending" ? (
                        <CaretUpFill />
                      ) : (
                        <CaretDownFill />
                      )
                    ) :  (
                      <>
                        <CaretUpFill className="text-muted" />
                        <CaretDownFill className="text-muted" />
                      </>
                    )}
                  </span>
                )}
                </span>
              </th>
            ))}
            {actions.status && (
              <th style={{ width: "90px" }} className="col-heading">
                Status
              </th>
            )}
            {showActionsColumn && (
              <th style={{ width: "90px" }} className="col-heading">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {safeFilteredData.length === 0 ? (
            <tr>
              <td
                colSpan={
                  columns.length +
                  (actions.checkbox ? 1 : 0) +
                  (showActionsColumn ? 1 : 0)
                }
                className="text-center"
              >
                No Data Found.
              </td>
            </tr>
          ) : (
            paginatedData.map((item) => (
              <tr key={item.id}>
                {actions.checkbox && (
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selectedRows.has(item.id)}
                      onChange={(e:React.ChangeEvent<HTMLInputElement>) => {
                        handleCheckboxChange(item.id)
                        e.currentTarget.blur()
                      }}
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td
                    key={column.key}
                    onClick={() => {
                      if (column.onClick) {
                        column.onClick(item);
                      }
                    }}
                    title={
                      column.dataType === "string"
                        ? String(item[column.key as keyof T])
                        : undefined
                    }
                    // style={{
                    //   cursor: column.onClick ? "pointer" : "default",
                    //   width: `calc(${column.label.length}ch + 20px)`,
                    // }}
                    style={{
                      cursor: column.onClick ? "pointer" : "default",
                      width: `calc(${column.label.length}ch + 20px)`,
                      maxWidth: "200px",
                      // whiteSpace: column.dataType === "string" ? "nowrap" : "normal", 
                      // overflow: column.dataType === "string" ? "hidden" : "visible",
                      // textOverflow: column.dataType === "string" ? "ellipsis" : "clip", 
                    }}
                  >
                    {column.render ? (
                      column.render(item)
                    ) : column.dataType === "image" ? (
                      <img
                        src={item[column.key as keyof T] as unknown as string}
                        alt={column.label}
                        style={{ width: "50px" }}
                      />
                    ) : column.dataType === "button" && column.button ? (
                      <Button
                        className="btn table-btn"
                        onClick={() => column.button?.onClick(item)}
                        disabled={
                          column.button.disabled
                            ? column.button.disabled(item)
                            : false
                        }
                      >
                        {typeof column.button.label === "function"
                          ? column.button.label(item)
                          : column.button.label || column.button.icon}
                      </Button>
                    ) : column.dataType === "toggle" && onToggle ? (
                      <Form.Check
                        type="switch"
                        id={`show-result-switch-${item.id}`}
                        className="toggle-switch slider-round custom-switch"
                        checked={item[column.key as keyof T] === 1}
                        onChange={(ev) => {
                          if(onToggle){
                          onToggle(item)
                          }
                          ev.currentTarget.blur()
                        }}
                      />
                    ) : (
                      String(item[column.key as keyof T])
                    )}
                  </td>
                ))}
                {actions.status && onStatusToggle && (
                  <td>
                    <Form.Check
                      type="switch"
                      id={`status-switch-${item.id}`}
                      className="toggle-switch slider-round custom-switch"
                      checked={item.status}
                      onChange={(ev) => {
                        onStatusToggle(item)
                        ev.currentTarget.blur()
                      }}
                    />
                  </td>
                )}
                {showActionsColumn && (
                  <td>
                    {actions.view && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => onView && onView(item)}
                        className="p-0 icon border-0"
                      >
                        <Eye />
                      </Button>
                    )}
                    {actions.edit && (
                      <button
                        onClick={() => handleEdit(item)}
                        className="delete-icon-ct p-0 icon border-0"
                      >
                        <PencilSquare />
                      </button>
                    )}
                    {actions.delete && (
                      <button
                        onClick={() =>
                          handleShowModal(
                            <DeleteModal
                              item={item}
                              onDelete={() => onDelete?.(item)}
                              onClose={handleCloseModal}
                            />
                          )
                        }
                        className="delete-icon-ct p-0 icon border-0"
                      >
                        <Trash3Fill />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          {safeFilteredData.length === 0 ||
          (apiPagination && totalPageCount === 0) ? (
            <span>Showing 0 to 0 of 0 Records</span>
          ) : (
            <span>
              Showing {page * rowsPerPage + 1} to{" "}
              {Math.min(
                (page + 1) * rowsPerPage,
                apiPagination ? totalPageCount : safeFilteredData.length
              )}{" "}
              of {apiPagination ? totalPageCount : safeFilteredData.length}{" "}
              Records
            </span>
          )}
        </div>

        {safeFilteredData.length === 0 ||
        (apiPagination && totalPageCount === 0) ? (
          <div></div>
        ) : (
          pagination &&
          totalPages > 1 && (
            <Pagination>
              {page !== 0 && (
                <Pagination.Item onClick={() => handleChangePage(page - 1)}>
                  Previous
                </Pagination.Item>
              )}
              {(() => {
                const maxVisiblePages = 5;

                let startPage = Math.max(
                  0,
                  page - Math.floor(maxVisiblePages / 2)
                );
                let endPage = Math.min(totalPages, startPage + maxVisiblePages);

                if (endPage > totalPages) {
                  endPage = totalPages;
                }

                if (endPage - startPage < maxVisiblePages) {
                  startPage = Math.max(0, endPage - maxVisiblePages);
                }

                return Array.from({ length: endPage - startPage }, (_, i) => {
                  const pageIndex = startPage + i;
                  return (
                    <Pagination.Item
                      key={pageIndex}
                      active={pageIndex === page}
                      onClick={() => handleChangePage(pageIndex)}
                    >
                      {pageIndex + 1}
                    </Pagination.Item>
                  );
                });
              })()}

              {page !== totalPages - 1 && (
                <Pagination.Item onClick={() => handleChangePage(page + 1)}>
                  Next
                </Pagination.Item>
              )}
            </Pagination>
          )
        )}
      </div>
      <div className="mb-3"></div>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalContent}</Modal.Body>
      </Modal>
    </div>
  );
};

const ViewModal = ({ item, onClose }: { item: any; onClose: () => void }) => (
  <div>
    <h5>View Item</h5>
    <pre>{JSON.stringify(item, null, 2)}</pre>
    <Button variant="secondary" onClick={onClose}>
      Close
    </Button>
  </div>
);

const EditModal = ({
  item,
  onEdit,
  onClose,
}: {
  item: any;
  onEdit?: (item: any) => void;
  onClose: () => void;
}) => {
  const [editedItem, setEditedItem] = useState(item);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedItem((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    if (onEdit) onEdit(editedItem);
    onClose();
  };

  return (
    <div>
      <h5>Edit Item</h5>
      {Object.keys(item).map((key) => (
        <Form.Group key={key} className="mb-3">
          <Form.Label>{key}</Form.Label>
          <Form.Control
            type="text"
            name={key}
            value={editedItem[key]}
            onChange={handleChange}
          />
        </Form.Group>
      ))}
      <Button variant="primary" onClick={handleSave}>
        Save
      </Button>
      <Button variant="secondary" onClick={onClose} className="ms-2">
        Cancel
      </Button>
    </div>
  );
};

export const DeleteModal = ({
  item,
  onDelete,
  onClose,
}: {
  item: any;
  onDelete?: (item: any) => void;
  onClose: () => void;
}) => (
  <div>
    <p>Are you sure you want to delete?</p>
    <Button
      variant="danger"
      onClick={() => {
        if (onDelete) onDelete(item);
        onClose();
      }}
    >
      Delete
    </Button>
    <Button variant="secondary" onClick={onClose} className="ms-2">
      Cancel
    </Button>
  </div>
);

export default CommonTable;
